import { createSlice } from "@reduxjs/toolkit";
import type { AdminSliceTypes } from "../../types/admin/admin.model";
import type {
  createAdminResponse,
  deleteAdminResponse,
  getAdminResponse,
  InfoAdminResponse,
  ListAdminResponse,
  LoginResponseTypes,
  updateAdminResponse,
  ChangePasswordResponse,
  UpdateProfileResponse,
  ToggleStatusResponse,
  ForceLogoutResponse,
} from "../../types/admin/admin.response";
import type {
  createAdminPayload,
  ListAdminPayload,
  LoginPayloadTypes,
  updateAdminPayload,
  ChangePasswordPayload,
  UpdateProfilePayload,
  ToggleStatusPayload,
} from "../../types/admin/admin.payload";
import { axiosClient } from "../../configs/axiosClient";
import { API } from "../../configs/endpoints";
import { addAsyncCases, createApiThunk } from "../createApiThunk";

const initialState: AdminSliceTypes = {
  loading: {
    login: false,
    logout: false,
    refresh: false,
    list: false,
    detail: false,
    create: false,
    update: false,
    delete: false,
    toggleStatus: false,
    forceLogout: false,
    changePassword: false,
    updateProfile: false,
  },
  error: null,
  admin: null,
  admins: [],
  selectedAdmin: null,
  pagination: null,
  accessToken: null,
  isInitialized: false,
};

export const login = createApiThunk<LoginResponseTypes, LoginPayloadTypes>(
  "admin/login",
  async (data) => (await axiosClient.post(API.admin.login, data)).data,
  "Login thất bại",
);

export const refreshToken = createApiThunk<
  { accessToken: string; admin: InfoAdminResponse },
  void
>(
  "admin/refreshToken",
  async () => {
    const refresh = await axiosClient.post(API.admin.refresh);
    const accessToken = refresh.data.newAccessToken;
    const me = await axiosClient.get(API.admin.me, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return { accessToken, admin: me.data.me };
  },
  "Lấy thông tin cá nhân thất bại",
);

export const list = createApiThunk<ListAdminResponse, ListAdminPayload>(
  "admin/list",
  async (query) =>
    (await axiosClient.get(API.admin.list, { params: query })).data,
  "Lấy danh sách admin thất bại",
);

export const createAdmin = createApiThunk<
  createAdminResponse,
  createAdminPayload
>(
  "admin/createAdmin",
  async (data) => (await axiosClient.post(API.admin.create, data)).data,
  "Tạo admin thất bại",
);

export const updateAdmin = createApiThunk<
  updateAdminResponse,
  { id: string; data: updateAdminPayload }
>(
  "admin/updateAdmin",
  async ({ id, data }) =>
    (await axiosClient.put(API.admin.update(id), data)).data,
  "Cập nhật admin thất bại",
);

export const getAdmin = createApiThunk<getAdminResponse, string>(
  "admin/getAdmin",
  async (id) => (await axiosClient.get(API.admin.detail(id))).data,
  "Lấy thông tin admin thất bại",
);

export const deleteAdmin = createApiThunk<deleteAdminResponse, string>(
  "admin/deleteAdmin",
  async (id) => (await axiosClient.delete(API.admin.delete(id))).data,
  "Xóa admin thất bại",
);

export const toggleStatus = createApiThunk<
  ToggleStatusResponse,
  ToggleStatusPayload
>(
  "admin/toggleStatus",
  async ({ id, isActive }) =>
    (await axiosClient.patch(API.admin.toggleStatus(id), { isActive })).data,
  "Cập nhật trạng thái thất bại",
);

export const forceLogoutAdmin = createApiThunk<
  ForceLogoutResponse & { targetId: string },
  string
>(
  "admin/forceLogout",
  async (id) => {
    const response = await axiosClient.post(API.admin.forceLogout(id));
    return { ...response.data, targetId: id };
  },
  "Force logout thất bại",
);

export const changePassword = createApiThunk<
  ChangePasswordResponse,
  ChangePasswordPayload
>(
  "admin/changePassword",
  async (data) =>
    (await axiosClient.put(API.admin.changePassword, data)).data,
  "Đổi mật khẩu thất bại",
);

export const updateProfile = createApiThunk<
  UpdateProfileResponse,
  UpdateProfilePayload
>(
  "admin/updateProfile",
  async (data) => (await axiosClient.put(API.admin.updateProfile, data)).data,
  "Cập nhật hồ sơ thất bại",
);

export const logout = createApiThunk<string, void>(
  "admin/logout",
  async () => (await axiosClient.post(API.admin.logout)).data,
  "Logout thất bại",
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    clearSelectedAdmin: (state) => {
      state.selectedAdmin = null;
    },
    forceLogoutLocal: (state) => {
      state.admin = null;
      state.accessToken = null;
      state.isInitialized = true;
    },
  },
  extraReducers: (builder) => {
    addAsyncCases({
      builder,
      thunk: login,
      loadingKey: "login",
      fallbackError: "Login thất bại",
      onSuccess: (state, payload) => {
        state.admin = payload.admin;
        state.accessToken = payload.access_token;
      },
    });
    addAsyncCases({
      builder,
      thunk: refreshToken,
      loadingKey: "refresh",
      fallbackError: "Lấy thông tin thất bại",
      onSuccess: (state, payload) => {
        state.accessToken = payload.accessToken;
        state.admin = payload.admin;
        state.isInitialized = true;
      },
      onError: (state) => {
        state.isInitialized = true;
      },
    });
    addAsyncCases({
      builder,
      thunk: list,
      loadingKey: "list",
      fallbackError: "Lấy danh sách admin thất bại",
      onSuccess: (state, payload) => {
        state.admins = payload.admins;
        state.pagination = payload.pagination;
      },
    });
    addAsyncCases({
      builder,
      thunk: createAdmin,
      loadingKey: "create",
      fallbackError: "Tạo admin thất bại",
      onSuccess: (state, payload) => {
        state.admins.push(payload.admin);
      },
    });
    addAsyncCases({
      builder,
      thunk: updateAdmin,
      loadingKey: "update",
      fallbackError: "Cập nhật admin thất bại",
      onSuccess: (state, payload) => {
        const updated = payload.updatedAdmin;
        const idx = state.admins.findIndex((a) => a._id === updated._id);
        if (idx !== -1) state.admins[idx] = updated;
      },
    });
    addAsyncCases({
      builder,
      thunk: getAdmin,
      loadingKey: "detail",
      fallbackError: "Lấy thông tin admin thất bại",
      onSuccess: (state, payload) => {
        state.selectedAdmin = payload.admin;
      },
    });
    addAsyncCases({
      builder,
      thunk: deleteAdmin,
      loadingKey: "delete",
      fallbackError: "Xóa admin thất bại",
      onSuccess: (state, payload) => {
        state.admins = state.admins.filter(
          (a) => a._id !== payload.deletedAdmin,
        );
      },
    });
    addAsyncCases({
      builder,
      thunk: toggleStatus,
      loadingKey: "toggleStatus",
      fallbackError: "Cập nhật trạng thái thất bại",
      onSuccess: (state, payload) => {
        const idx = state.admins.findIndex((a) => a._id === payload.admin._id);
        if (idx !== -1) state.admins[idx] = payload.admin;
      },
    });
    addAsyncCases({
      builder,
      thunk: forceLogoutAdmin,
      loadingKey: "forceLogout",
      fallbackError: "Force logout thất bại",
    });
    addAsyncCases({
      builder,
      thunk: changePassword,
      loadingKey: "changePassword",
      fallbackError: "Đổi mật khẩu thất bại",
      onSuccess: (state) => {
        state.admin = null;
        state.accessToken = null;
      },
    });
    addAsyncCases({
      builder,
      thunk: updateProfile,
      loadingKey: "updateProfile",
      fallbackError: "Cập nhật hồ sơ thất bại",
      onSuccess: (state, payload) => {
        state.admin = payload.admin;
      },
    });
    addAsyncCases({
      builder,
      thunk: logout,
      loadingKey: "logout",
      fallbackError: "Logout thất bại",
      onSuccess: (state) => {
        state.admin = null;
        state.accessToken = null;
      },
    });
  },
});

export const { setAccessToken, clearSelectedAdmin, forceLogoutLocal } =
  adminSlice.actions;
export default adminSlice.reducer;
